import { apiClient } from './apiClient';
import { hybridUploadService } from './hybridUploadService';
import { User, Site, StatusReport } from '../types';
import { loggingService } from '../utils/loggingService';

// Define the report submission interface - UPDATED to use URLs only
interface ReportSubmission {
  siteId: string;
  taskCompletions: {
    taskId: string;
    notes: string;
    imageFile?: File; // Changed from imageBase64 to imageFile (legacy support)
    imageFiles?: File[]; // FIXED: Array of image files for multiple images per task
    imageUrls?: string[]; // Array of image URLs (replaces images and additionalImagesBase64)
    latitude: number;
    longitude: number;
    incidentStatus?: string;
    userId?: string; // Optional user ID for each task completion
    photoRequirement?: number; // PhotoRequirement enum value (0=Required, 1=Optional, 2=None)
  }[];
  isTaskCompletion?: boolean; // Indicates if this report represents a task completion
  isActive?: boolean; // Indicates if this task completion should be considered "current"
}

// Progress callback interface for image uploads
interface ImageUploadProgressCallback {
  (imageIndex: number, status: 'uploading' | 'completed' | 'error', details?: string): void;
}

/**
 * Service for site report management - COMPLETELY REWRITTEN to eliminate base64
 * 
 * NEW WORKFLOW:
 * 1. Upload images directly to S3/Cloudinary
 * 2. Get URLs from upload service
 * 3. Send only URLs to backend (no base64 data)
 * 4. Backend stores URLs in database
 */
export class SiteReportsService {
  /**
   * Submit a report for a site with task completions using URL-only approach
   * @param siteId The ID of the site to submit the report for
   * @param reportData The report data to submit
   * @param userId The ID of the user submitting the report (optional, will try to get from reportData or localStorage as fallback)
   * @param onImageProgress Optional callback to track individual image upload progress
   */
  async submitReport(siteId: string, reportData: ReportSubmission, userId?: string, onImageProgress?: ImageUploadProgressCallback, reportedAt?: string): Promise<boolean> {
    console.log('🚀 Starting URL-only report submission for site:', siteId);
    
    // Log the start of report submission
    loggingService.logUserAction('ReportSubmission_URLOnly_Start', {
      siteId,
      taskCompletionsCount: reportData.taskCompletions.length,
      isTaskCompletion: reportData.isTaskCompletion,
      isActive: reportData.isActive,
      hasUserId: !!userId,
      timestamp: new Date().toISOString()
    });

    // Get user ID
    let userIdToUse = userId;
    if (!userIdToUse) {
      const userIdFromCompletions = reportData.taskCompletions.find(tc => tc.userId)?.userId;
      if (userIdFromCompletions) {
        userIdToUse = userIdFromCompletions;
      } else {
        const currentUserJson = localStorage.getItem('user');
        userIdToUse = currentUserJson ? JSON.parse(currentUserJson).id : localStorage.getItem('userId');
      }
    }
    
    if (!userIdToUse) {
      console.error('❌ No user ID found for report submission');
      return false;
    }

    try {
      // Step 1: Upload all images to S3/Cloudinary and collect URLs
      const imageUrls = await this.uploadAllImages(reportData, onImageProgress);
      
      // Step 2: Create report request with URLs only
      const reportRequest = this.createReportRequest(siteId, reportData, userIdToUse, imageUrls, reportedAt);
      
      // Step 3: Submit report with URLs to backend
      const success = await this.submitReportToBackend(reportRequest);
      
      if (success) {
        console.log('✅ Report submitted successfully using URL-only approach');
        
        loggingService.logUserAction('ReportSubmission_URLOnly_Success', {
          siteId,
          totalImagesUploaded: imageUrls.length,
          timestamp: new Date().toISOString()
        });
      } else {
        console.error('❌ Failed to submit report to backend');
        
        loggingService.logUserAction('ReportSubmission_URLOnly_Failed', {
          siteId,
          error: 'Backend submission failed',
          timestamp: new Date().toISOString()
        });
      }
      
      return success;

    } catch (error: any) {
      console.error('❌ Error in URL-only report submission:', error);
      
      loggingService.logUserAction('ReportSubmission_URLOnly_Exception', {
        siteId,
        error: error?.message || String(error),
        timestamp: new Date().toISOString()
      });
      
      return false;
    }
  }

  /**
   * Upload all images from task completions to S3/Cloudinary
   * ENHANCED: Handles PhotoRequirement settings properly (Required, Optional, None)
   * @param reportData The report data containing images
   * @param onImageProgress Optional progress callback
   * @returns Array of uploaded image URLs
   * @throws Error if any required image upload fails
   */
  private async uploadAllImages(reportData: ReportSubmission, onImageProgress?: ImageUploadProgressCallback): Promise<string[]> {
    console.log('📤 Starting ENHANCED image uploads with PhotoRequirement support...');
    
    const allImageUrls: string[] = [];
    let imageIndex = 1;
    const imagesToUpload: { file: File; taskId: string; index: number; isRequired: boolean }[] = [];

    // First, collect all images that need to be uploaded
    for (const completion of reportData.taskCompletions) {
      // Determine if images are required for this task (default to Required for backward compatibility)
      const photoRequirement = completion.photoRequirement ?? 0; // Default to Required (0)
      const isImageRequired = photoRequirement === 0; // PhotoRequirement.Required = 0
      const isImageOptional = photoRequirement === 1; // PhotoRequirement.Optional = 1
      const isImageNone = photoRequirement === 2; // PhotoRequirement.None = 2
      
      console.log(`📤 Task ${completion.taskId} PhotoRequirement: ${photoRequirement} (Required: ${isImageRequired}, Optional: ${isImageOptional}, None: ${isImageNone})`);
      
      // Skip image processing entirely for PhotoRequirement.None tasks
      if (isImageNone) {
        console.log(`📤 Skipping image processing for task ${completion.taskId} (PhotoRequirement.None)`);
        continue;
      }
      
      // FIXED: Handle imageFiles array (multiple images per task)
      if (completion.imageFiles && completion.imageFiles.length > 0) {
        console.log(`📤 Found ${completion.imageFiles.length} images for task ${completion.taskId}`);
        for (const imageFile of completion.imageFiles) {
          imagesToUpload.push({
            file: imageFile,
            taskId: completion.taskId,
            index: imageIndex++,
            isRequired: isImageRequired
          });
        }
      }
      
      // LEGACY: Also handle single imageFile for backward compatibility
      if (completion.imageFile && (!completion.imageFiles || completion.imageFiles.length === 0)) {
        console.log(`📤 Found single legacy image for task ${completion.taskId}`);
        imagesToUpload.push({
          file: completion.imageFile,
          taskId: completion.taskId,
          index: imageIndex++,
          isRequired: isImageRequired
        });
      }
      
      // Handle additional images if they're already URLs
      if (completion.imageUrls && completion.imageUrls.length > 0) {
        console.log(`📤 Adding ${completion.imageUrls.length} existing image URLs for task ${completion.taskId}...`);
        allImageUrls.push(...completion.imageUrls);
      }
      
      // Check if required images are missing
      if (isImageRequired && 
          (!completion.imageFiles || completion.imageFiles.length === 0) && 
          !completion.imageFile && 
          (!completion.imageUrls || completion.imageUrls.length === 0)) {
        const errorMessage = `Task ${completion.taskId} requires images but none were provided`;
        console.error(`❌ VALIDATION ERROR: ${errorMessage}`);
        
        loggingService.logUserAction('ReportSubmission_RequiredImage_Missing', {
          taskId: completion.taskId,
          photoRequirement,
          timestamp: new Date().toISOString()
        });
        
        throw new Error(`VALIDATION ERROR: ${errorMessage}. Please add images to required tasks.`);
      }
    }

    // If no images to upload, return existing URLs (this is now valid for PhotoRequirement.None tasks)
    if (imagesToUpload.length === 0) {
      console.log('📤 No new images to upload, using existing URLs only (valid for PhotoRequirement.None/Optional tasks)');
      return allImageUrls;
    }

    console.log(`📤 Found ${imagesToUpload.length} images to upload. Required images must succeed, optional images will be attempted.`);

    // Upload each image with enhanced error handling based on PhotoRequirement
    for (const imageToUpload of imagesToUpload) {
      console.log(`📤 Uploading image ${imageToUpload.index} for task ${imageToUpload.taskId} (Required: ${imageToUpload.isRequired})...`);
      
      if (onImageProgress) {
        onImageProgress(imageToUpload.index, 'uploading', `Uploading image ${imageToUpload.index}`);
      }
      
      try {
        const fileName = `report_${Date.now()}_task_${imageToUpload.taskId}_img${imageToUpload.index}.jpg`;
        const uploadResult = await hybridUploadService.uploadFile(imageToUpload.file, 'reports');
        
        if (uploadResult.success && (uploadResult.downloadUrl || uploadResult.publicUrl)) {
          const imageUrl = uploadResult.downloadUrl || uploadResult.publicUrl || '';
          allImageUrls.push(imageUrl);
          
          console.log(`✅ Image ${imageToUpload.index} uploaded successfully: ${imageUrl}`);
          
          if (onImageProgress) {
            onImageProgress(imageToUpload.index, 'completed', `Image ${imageToUpload.index} uploaded successfully`);
          }
          
          loggingService.logUserAction('ReportSubmission_ImageUpload_Success', {
            taskId: imageToUpload.taskId,
            imageIndex: imageToUpload.index,
            imageUrl,
            uploadMethod: 'cloudinary',
            isRequired: imageToUpload.isRequired,
            timestamp: new Date().toISOString()
          });
        } else {
          // Enhanced error handling based on PhotoRequirement
          const errorMessage = `Image ${imageToUpload.index} upload failed: ${uploadResult.error || 'Unknown upload error'}`;
          
          if (imageToUpload.isRequired) {
            // REQUIRED: Failure causes entire process to fail
            console.error(`❌ REQUIRED IMAGE FAILURE: ${errorMessage}`);
            
            if (onImageProgress) {
              onImageProgress(imageToUpload.index, 'error', errorMessage);
            }
            
            loggingService.logUserAction('ReportSubmission_RequiredImageUpload_Failed', {
              taskId: imageToUpload.taskId,
              imageIndex: imageToUpload.index,
              error: uploadResult.error || 'Unknown upload error',
              timestamp: new Date().toISOString()
            });
            
            throw new Error(`REQUIRED IMAGE FAILURE: ${errorMessage}. Report submission cancelled.`);
          } else {
            // OPTIONAL: Log warning but continue
            console.warn(`⚠️ OPTIONAL IMAGE FAILURE: ${errorMessage} - continuing with submission`);
            
            if (onImageProgress) {
              onImageProgress(imageToUpload.index, 'error', `Optional image failed: ${errorMessage}`);
            }
            
            loggingService.logUserAction('ReportSubmission_OptionalImageUpload_Failed', {
              taskId: imageToUpload.taskId,
              imageIndex: imageToUpload.index,
              error: uploadResult.error || 'Unknown upload error',
              timestamp: new Date().toISOString()
            });
          }
        }
      } catch (error: any) {
        // Enhanced exception handling based on PhotoRequirement
        const errorMessage = `Image ${imageToUpload.index} upload exception: ${error?.message || 'Unknown error'}`;
        
        if (imageToUpload.isRequired) {
          // REQUIRED: Exception causes entire process to fail
          console.error(`❌ REQUIRED IMAGE EXCEPTION: ${errorMessage}`);
          
          if (onImageProgress) {
            onImageProgress(imageToUpload.index, 'error', errorMessage);
          }
          
          loggingService.logUserAction('ReportSubmission_RequiredImageUpload_Exception', {
            taskId: imageToUpload.taskId,
            imageIndex: imageToUpload.index,
            error: error?.message || 'Unknown error',
            timestamp: new Date().toISOString()
          });
          
          throw new Error(`REQUIRED IMAGE EXCEPTION: ${errorMessage}. Report submission cancelled.`);
        } else {
          // OPTIONAL: Log warning but continue
          console.warn(`⚠️ OPTIONAL IMAGE EXCEPTION: ${errorMessage} - continuing with submission`);
          
          if (onImageProgress) {
            onImageProgress(imageToUpload.index, 'error', `Optional image failed: ${errorMessage}`);
          }
          
          loggingService.logUserAction('ReportSubmission_OptionalImageUpload_Exception', {
            taskId: imageToUpload.taskId,
            imageIndex: imageToUpload.index,
            error: error?.message || 'Unknown error',
            timestamp: new Date().toISOString()
          });
        }
      }
    }

    console.log(`✅ ENHANCED UPLOAD SUCCESS: Processed ${imagesToUpload.length} images. Total URLs: ${allImageUrls.length}`);
    return allImageUrls;
  }

  /**
   * Create the report request object with URLs only - FIXED to match CreateReportDto exactly
   * @param siteId Site ID
   * @param reportData Original report data
   * @param userId User ID
   * @param imageUrls Array of uploaded image URLs
   * @returns Report request object
   */
  private createReportRequest(siteId: string, reportData: ReportSubmission, userId: string, imageUrls: string[], reportedAt?: string) {
    const primaryImageUrl = imageUrls.length > 0 ? imageUrls[0] : null;
    const additionalImageUrls = imageUrls.slice(1);

    // Validate and format GUIDs - IMPROVED validation
    const validateGuid = (id: string, fieldName: string): string => {
      if (!id || typeof id !== 'string') {
        throw new Error(`${fieldName} is required and must be a valid string`);
      }
      
      // Handle both formats: with and without dashes
      const cleanId = id.replace(/-/g, '');
      
      // Validate GUID format (32 hex characters)
      if (!/^[0-9a-fA-F]{32}$/.test(cleanId)) {
        throw new Error(`${fieldName} must be a valid GUID format (got: ${id})`);
      }
      
      // Re-format as proper GUID with dashes
      return `${cleanId.substring(0, 8)}-${cleanId.substring(8, 12)}-${cleanId.substring(12, 16)}-${cleanId.substring(16, 20)}-${cleanId.substring(20, 32)}`.toLowerCase();
    };

    try {
      // Get the first task completion for data
      const firstTask = reportData.taskCompletions[0];
      if (!firstTask) {
        throw new Error('At least one task completion is required');
      }

      // Create request object that EXACTLY matches CreateReportDto structure
      const reportRequest = {
        // Required GUID fields - properly formatted and validated
        userId: validateGuid(userId, 'UserId'),
        siteId: validateGuid(siteId, 'SiteId'),
        siteTaskId: firstTask.taskId ? validateGuid(firstTask.taskId, 'SiteTaskId') : null,
        
        // Required string fields with proper defaults
        notes: firstTask.notes || '',
        incidentStatus: firstTask.incidentStatus === 'incident' ? 'extreme' : (firstTask.incidentStatus || 'normal'), // Map 'incident' to 'extreme' for backend
        
        // Required number fields with validation
        latitude: typeof firstTask.latitude === 'number' ? firstTask.latitude : 0,
        longitude: typeof firstTask.longitude === 'number' ? firstTask.longitude : 0,
        
        // Optional fields that match DTO exactly
        smartRevision: null, // Will be generated by backend
        reportedAt: reportedAt || new Date().toISOString(), // Use passed timestamp or current time
        reviewStatus: false,
        isTaskCompletion: reportData.isTaskCompletion === true,
        isActive: reportData.isActive !== false, // Default to true unless explicitly false
        
        // S3 Upload Support fields - handle null vs empty string properly
        compressedImageUrl: primaryImageUrl,
        fullImageUrl: primaryImageUrl,
        additionalImageUrls: additionalImageUrls.length > 0 ? additionalImageUrls : null,
        uploadMethod: 's3-presigned-url'
      };

      console.log('📋 Created report request (IMPROVED structure):', {
        siteId: reportRequest.siteId,
        userId: reportRequest.userId,
        siteTaskId: reportRequest.siteTaskId,
        notes: reportRequest.notes.substring(0, 50) + (reportRequest.notes.length > 50 ? '...' : ''),
        incidentStatus: reportRequest.incidentStatus,
        latitude: reportRequest.latitude,
        longitude: reportRequest.longitude,
        isTaskCompletion: reportRequest.isTaskCompletion,
        isActive: reportRequest.isActive,
        hasCompressedImageUrl: !!reportRequest.compressedImageUrl,
        hasFullImageUrl: !!reportRequest.fullImageUrl,
        additionalImageUrlsCount: reportRequest.additionalImageUrls?.length || 0,
        uploadMethod: reportRequest.uploadMethod
      });

      return reportRequest;
    } catch (error) {
      console.error('❌ Error creating report request:', error);
      throw new Error(`Failed to create report request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Submit the report request to the backend
   * @param reportRequest The report request with URLs
   * @returns True if successful
   */
  private async submitReportToBackend(reportRequest: any): Promise<boolean> {
    console.log('📡 Submitting report to backend...');
    
    try {
      // Calculate request size (should be very small now)
      const requestBody = JSON.stringify(reportRequest);
      const requestSizeKB = Math.round(requestBody.length / 1024);
      
      console.log(`📡 Request size: ${requestSizeKB}KB (should be <10KB with URL-only approach)`);
      console.log('📡 Request payload structure:', {
        hasUserId: !!reportRequest.userId,
        hasSiteId: !!reportRequest.siteId,
        hasSiteTaskId: !!reportRequest.siteTaskId,
        hasNotes: !!reportRequest.notes,
        hasIncidentStatus: !!reportRequest.incidentStatus,
        hasLatitude: typeof reportRequest.latitude === 'number',
        hasLongitude: typeof reportRequest.longitude === 'number',
        hasCompressedImageUrl: !!reportRequest.compressedImageUrl,
        hasFullImageUrl: !!reportRequest.fullImageUrl,
        hasAdditionalImageUrls: !!reportRequest.additionalImageUrls,
        hasUploadMethod: !!reportRequest.uploadMethod
      });
      
      // CRITICAL DEBUG: Log the actual request body being sent
      console.log('📡 CRITICAL DEBUG - Request body length:', requestBody.length);
      console.log('📡 CRITICAL DEBUG - Request body preview:', requestBody.substring(0, 200) + '...');
      console.log('📡 CRITICAL DEBUG - Full request object keys:', Object.keys(reportRequest));
      console.log('📡 CRITICAL DEBUG - Request object values check:', {
        userId: reportRequest.userId,
        siteId: reportRequest.siteId,
        notes: reportRequest.notes?.substring(0, 50),
        incidentStatus: reportRequest.incidentStatus
      });
      
      loggingService.logUserAction('ReportSubmission_BackendAttempt', {
        siteId: reportRequest.siteId,
        requestSizeKB,
        uploadMethod: reportRequest.uploadMethod,
        requestBodyLength: requestBody.length,
        timestamp: new Date().toISOString()
      });

      // FIXED: Send the data directly as CreateReportDto (not wrapped)
      // The backend expects [FromBody] CreateReportDto directly
      // CRITICAL FIX: Remove explicit Content-Type header to avoid conflicts
      const response = await apiClient.post<void>('/reports', reportRequest);
      
      if (response.success) {
        console.log('✅ Report submitted to backend successfully');
        
        loggingService.logUserAction('ReportSubmission_BackendSuccess', {
          siteId: reportRequest.siteId,
          requestSizeKB,
          timestamp: new Date().toISOString()
        });
        
        return true;
      } else {
        console.error('❌ Backend rejected report submission:', response.message);
        console.error('❌ Backend response details:', response);
        
        loggingService.logUserAction('ReportSubmission_BackendRejected', {
          siteId: reportRequest.siteId,
          error: response.message,
          requestSizeKB,
          timestamp: new Date().toISOString()
        });
        
        return false;
      }
    } catch (error: any) {
      console.error('❌ Network error submitting to backend:', error);
      console.error('❌ Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      
      loggingService.logUserAction('ReportSubmission_BackendNetworkError', {
        siteId: reportRequest.siteId,
        error: error?.message || String(error),
        timestamp: new Date().toISOString()
      });
      
      return false;
    }
  }

  /**
   * Convert base64 images to File objects for upload
   * This is a helper method for migrating existing code that still uses base64
   * @param base64Image Base64 image string
   * @param fileName Desired file name
   * @returns File object
   */
  base64ToFile(base64Image: string, fileName: string = 'image.jpg'): File {
    // Remove data URL prefix if present
    const base64Data = base64Image.includes(',') ? base64Image.split(',')[1] : base64Image;
    
    // Determine content type from data URL or default to JPEG
    let contentType = 'image/jpeg';
    if (base64Image.startsWith('data:')) {
      const match = base64Image.match(/data:([^;]+);/);
      if (match) {
        contentType = match[1];
      }
    }

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create File object
    return new File([byteArray], fileName, { type: contentType });
  }

  /**
   * LEGACY METHOD: Submit a report using the old base64 approach
   * This method is kept for backward compatibility but should be avoided
   * @deprecated Use submitReport with File objects instead
   */
  async submitReportLegacy(siteId: string, reportData: any, userId?: string): Promise<boolean> {
    console.warn('⚠️ Using legacy base64 submission method. This may cause nginx errors for large images.');
    console.warn('⚠️ Please migrate to the new URL-only approach using File objects.');
    
    // Log usage of legacy method
    loggingService.logUserAction('ReportSubmission_LegacyMethod_Used', {
      siteId,
      warning: 'Legacy base64 method used - should migrate to URL-only approach',
      timestamp: new Date().toISOString()
    });
    
    // For now, return false to force migration to new method
    // In the future, this could be removed entirely
    console.error('❌ Legacy base64 submission method is disabled. Please use the new URL-only approach.');
    return false;
  }
}

// Create a default instance
export const siteReportsService = new SiteReportsService();

export default siteReportsService;
